import { getReporter } from './reporter'

/** 错误信息最大长度 */
const MAX_MESSAGE_LEN = 256
/** 调用栈最大长度 */
const MAX_STACK_LEN = 512

/** 基于 message+source+lineno 生成 8 字符错误指纹 */
function generateFingerprint(message: string, source: string, lineno: number): string {
  const raw = `${message}|${source}|${lineno}`
  let hash = 0
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) - hash + raw.charCodeAt(i)) | 0
  }
  return Math.abs(hash).toString(36).padStart(8, '0').slice(0, 8)
}

/**
 * 采集 JS 运行时错误和未处理的 Promise 拒绝
 *
 * 通过 window error 和 unhandledrejection 事件捕获，
 * 同一 flush 周期内相同错误自动去重
 */
export function setupErrors(): void {
  if (typeof window === 'undefined') return

  window.addEventListener(
    'error',
    event => {
      const reporter = getReporter()
      if (!reporter) return

      if (event.target && (event.target as HTMLElement).tagName) {
        const el = event.target as HTMLElement
        const message =
          `Failed to load ${el.tagName.toLowerCase()}: ${(el as HTMLImageElement).src || (el as HTMLLinkElement).href || ''}`.slice(
            0,
            MAX_MESSAGE_LEN
          )
        reporter.addError({
          type: 'resource',
          message,
          source: '',
          lineno: 0,
          colno: 0,
          stack: '',
          pagePath: location.pathname,
          timestamp: Date.now(),
          fingerprint: generateFingerprint(message, '', 0),
        })
        return
      }

      const message = (event.message || '').slice(0, MAX_MESSAGE_LEN)
      const source = normalizeSource(event.filename || '')
      const lineno = event.lineno || 0

      reporter.addError({
        type: 'js',
        message,
        source,
        lineno,
        colno: event.colno || 0,
        stack: extractStack(event.error),
        pagePath: location.pathname,
        timestamp: Date.now(),
        fingerprint: generateFingerprint(message, source, lineno),
      })
    },
    true
  )

  window.addEventListener('unhandledrejection', event => {
    const reporter = getReporter()
    if (!reporter) return

    const reason = event.reason
    const message = (reason instanceof Error ? reason.message : String(reason || '')).slice(
      0,
      MAX_MESSAGE_LEN
    )

    reporter.addError({
      type: 'promise',
      message,
      source: '',
      lineno: 0,
      colno: 0,
      stack: extractStack(reason),
      pagePath: location.pathname,
      timestamp: Date.now(),
      fingerprint: generateFingerprint(message, '', 0),
    })
  })
}

/** 提取错误调用栈并截断 */
function extractStack(error: unknown): string {
  if (error instanceof Error && error.stack) {
    return error.stack.slice(0, MAX_STACK_LEN)
  }
  return ''
}

/** 归一化错误来源文件路径 */
function normalizeSource(source: string): string {
  try {
    const url = new URL(source)
    return url.pathname
  } catch {
    return source
  }
}
